import { Bestek } from "@/models/bestek";
import BestekDetail from "@/pages/bestekken/detail/bestek_detail";
import { useState } from "react";
import { Modal, Spinner, Table, Form, InputGroup, Button } from "react-bootstrap";
import { PaginationControl } from "react-bootstrap-pagination-control";
import { useNavigate } from "react-router-dom";
import styles from "./bestek-list.module.scss";
import useAPIDownload from "@/hooks/download.hook";
import { connect } from "react-redux";
import { RootState } from "@/store";
import { BestekFilterState } from "@/store/reducers/bestek_filter";

interface Props {
    loading: boolean;
    bestekken: Bestek[];
    totalCount: number;
    pageSize?: number;
    showPageSizeSelector?: boolean;
    onPageSizeChange?: (pageSize: number) => void;
    page: number;
    onPageChange: (page: number) => void;
    openInModal?: boolean;
    extraColumns?: (header: boolean, bestek?: Bestek) => JSX.Element;
    filterState: BestekFilterState;
}

function mapStateToProps(state: RootState) {
    return {
        filterState: state.bestek_filter
    };
}

function BestekkenList(props: Props): JSX.Element {
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [showModalFor, setShowModalFor] = useState<Bestek>();
    const [pageInput, setPageInput] = useState<string>("");
    const downloadFromAPI = useAPIDownload();

    const totalPages = Math.ceil(props.totalCount / (props.pageSize ?? 10));

    const handleBestekClick = (bestek: Bestek) => {
        if (props.openInModal) {
            setShowModalFor(bestek);
            setShowModal(true);
        } else {
            const searchParams = new URLSearchParams();
            
            if (props.filterState.beschrijving && props.filterState.beschrijving.length > 0) {
                searchParams.set('searchTerms', props.filterState.beschrijving.join(','));
            }
            
            if (props.filterState.zoek_categorien && props.filterState.zoek_categorien.length > 0) {
                searchParams.set('searchCategories', props.filterState.zoek_categorien.map(c => c.value).join(','));
            }
            
            const queryString = searchParams.toString();
            const url = `/bestekken/${bestek.id}/${bestek.registratie_nummer}${queryString ? `?${queryString}` : ''}`;
            
            console.log('Navigating to detail page with search context:', {
                searchTerms: props.filterState.beschrijving,
                searchCategories: props.filterState.zoek_categorien?.map(c => c.value),
                url: url
            });
            
            navigate(url);
        }
    }

    const downloadBestek = (bestek: Bestek): void => {
        downloadFromAPI(
            { method: "get", path: `/bestek/download/${bestek.id}`, data: {} }, 
            `${bestek.registratie_nummer}.rsx`,
            {error: console.error}
        );
    }

    const handleGoToPage = () => {
        const pageNum = parseInt(pageInput);
        if (pageNum >= 1 && pageNum <= totalPages) {
            props.onPageChange(pageNum);
            setPageInput("");
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleGoToPage();
        }
    };
    
    return (
        <>
            <Table className="mt-2" size="sm" striped hover>
                <thead>
                    <tr>
                        <th style={{width: '15%'}}>Registratienummer</th>
                        <th>Beschrijving</th>
                        <th style={{width: '15%'}}>Release versie</th>
                        {
                            props.extraColumns && props.extraColumns(true)
                        }
                    </tr>
                </thead>
                <tbody>
                    {
                        props.loading && (
                            <tr>
                                <td colSpan={99} align="center">
                                    <Spinner size='sm' style={{marginRight: '5px'}}></Spinner>Bestekken zoeken...
                                </td>
                            </tr>
                        )
                    }
                    {
                        (props.bestekken !== null && props.bestekken.length === 0) && !props.loading && (
                            <tr>
                                <td colSpan={99} align="center">
                                    Er zijn geen bestekken die voldoen aan de zoekcriteria
                                </td>
                            </tr>
                        )
                    }
                    {
                        props.bestekken.map(bestek => {
                                return (
                                    <tr key={`bestek-id-${bestek.id}`} onClick={() => handleBestekClick(bestek)}>
                                        <td>{bestek.registratie_nummer}</td>
                                        <td>{bestek.beschrijving}</td>
                                        <td>{bestek.versie_release}</td>
                                        {
                                            props.extraColumns && props.extraColumns(false, bestek)
                                        }
                                    </tr>
                                )
                            })
                    }
                </tbody>
            </Table>

            {/* Enhanced pagination controls */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-3">
                {/* Page size selector */}
                {props.showPageSizeSelector && (
                    <div className="d-flex align-items-center">
                        <Form.Label className="mb-0 me-2 small">Toon:</Form.Label>
                        <Form.Select 
                            size="sm" 
                            value={props.pageSize ?? 10}
                            onChange={(e) => props.onPageSizeChange?.(parseInt(e.target.value))}
                            style={{ width: 'auto' }}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </Form.Select>
                        <span className="ms-2 small text-muted">per pagina</span>
                    </div>
                )}

                {/* Main pagination */}
                <div style={{ flex: 1, minWidth: '300px' }}>
                    <PaginationControl 
                        page={props.page}
                        between={4}
                        total={props.totalCount}
                        limit={props.pageSize ?? 10}
                        changePage={props.onPageChange}
                        ellipsis={1}
                    />
                </div>
                
                {/* Jump to page */}
                <div className="d-flex flex-column align-items-end">
                    <InputGroup size="sm" style={{ width: '100px' }}>
                        <Form.Control
                            type="number"
                            placeholder="Pagina"
                            value={pageInput}
                            onChange={(e) => setPageInput(e.target.value)}
                            onKeyPress={handleKeyPress}
                            min={1}
                            max={totalPages}
                        />
                        <Button 
                            variant="outline-secondary" 
                            onClick={handleGoToPage}
                            disabled={!pageInput}
                        >
                            Ga
                        </Button>
                    </InputGroup>
                    <small className="text-muted mt-1">
                        Pagina {props.page} van {totalPages}
                    </small>
                </div>
            </div>

            {/* Bestek detail modal */}
            <Modal show={showModal} fullscreen onHide={() => setShowModal(false)} dialogClassName={styles.modal}>
                <Modal.Header closeButton bsPrefix={styles.modal_header}>
                    <Modal.Title>
                        Bestek met registratienummer { showModalFor?.registratie_nummer }
                    </Modal.Title>
                    <div className={styles.download_button} onClick={() => downloadBestek(showModalFor as Bestek)}>Download</div>
                </Modal.Header>
                <Modal.Body>
                    <BestekDetail id={showModalFor ? showModalFor.id : 0} reg={showModalFor ? showModalFor.registratie_nummer : ""} modal />
                </Modal.Body>
            </Modal>
        </>
    )
}

export default connect(mapStateToProps)(BestekkenList);